// Example app of the KORU Salus frontend monorepo: the bare minimum of the KORU Nova kit
// to get in (auth, users, roles) + the kit's "Base data" (Dati di base) hub + the 14
// Salus base-data modules (the 13 taxonomies and legal entities), each with its own
// section, + the five REGISTRIES (companies, branches, brokers, suppliers and doctors),
// which are not base data at all: each has its own rail entry and its own top-level routes,
// + the sub-entities of an anagrafica (today the incarichi, the mansioni and the listini costi
// and prezzi of a company and the contratti di lavoro, mansioni, esenzioni, vaccinazioni and
// quadri scadenze of a worker), which are neither: they
// have no rail entry and no list of their own, and appear as TABS of the detail of the
// record that owns them — two PARENTS by now, the company and the worker, each with a
// `detailTabs` port of its own,
// + the OPERATIONAL modules — the Scadenzario, the visite mediche and their sessions, the external
// tests and their sessions, and the prestazioni — which are none of the three: rail entries and top-level routes like a registry,
// but in a group of their own (`services`), and the visits and the external tests each
// contribute a tab to THREE parents (worker, company and branch, the third `detailTabs` port),
// + the three FINANCE modules, the listini costi and prezzi and the fatture, which are none of
// the four: rail entries and top-level routes again, in the `financial` group, ordered right
// after `services`,
// + the REPORTS («Resoconti»), in none of the five: one page, `/analytics/reports`, with its rail
// entry in the kit's `analytics` group («Analisi»), right after `financial`,
// + the DASHBOARD, in none of the five: the home (`/`), which replaces the kit shell's
// placeholder, with no rail entry of its own (the rail's «Dashboard» button is the shell's),
// + the STATISTICS of a company and of a sede, in none of the five either: no route at all, just
// a «Statistiche» tab contributed to the company and to the branch through their ports,
// + the LOCAL CONNECTOR of the doctor's workstation, not a domain module at all (a package, no
// backend twin): only its indicator in the topbar.
//
// For a derived app: mount ONLY what you need. Two obligations to honour:
//   - `baseDataModule` must be mounted: without the hub, the Salus modules' sections
//     do not show up anywhere, with no error at all; the same goes for
//     `settingsModule` (config hub, `/settings`) and the Settings section of the
//     Salus `preferencesModule`, which replaces the kit's preferences module;
//   - whoever mounts a module with domain arcs (risks → norms/test-types, test-types →
//     test-type-categories/supplier, examination-limitations/-prescriptions → risks,
//     duty-templates → risks/test-types/survey-types, company-duties →
//     risks/test-types/survey-types + company,
//     company-contracts → company/branch/doctor/legal-entities,
//     company-cost-list → company/legal-entities/cost-list,
//     company-price-list → company/legal-entities/price-list,
//     employee → legal-entities/company/branch,
//     employee-contract → employee/company/branch,
//     employee-duty → employee/company-duties/risks/test-types/survey-types,
//     employee-exemption → employee,
//     employee-vaccination → employee/vaccination-types,
//     employee-expiration → employee/branch/test-types/survey-types,
//     examination → legal-entities/company/branch/employee/doctor/examination-types/risks/
//     test-types/survey-types,
//     examination-session → legal-entities/company/branch/doctor/employee/examination,
//     external-test → legal-entities/company/branch/employee,
//     external-test-session → legal-entities/company/branch/supplier,
//     service → legal-entities/company/branch/doctor/service-types,
//     scheduling → legal-entities,
//     cost-list → legal-entities/doctor/supplier + price-list-common,
//     price-list → legal-entities + price-list-common,
//     invoice → legal-entities/company/broker/service + price-list-common, preferences →
//     examination-types, dashboard → legal-entities,
//     company-stats → company/branch/legal-entities + price-list-common,
//     analytics → legal-entities) must INSTALL its
//     peers too;
//     mounting them is optional (their lookups are needed, not their sections).
//
// Every module exports its own `menuIcons`: they must be merged into `createApp` because
// `resolveMenuIcon` silently falls back to `Circle` for names outside the kit's
// curated set.

import { createApp } from "@spinning-top/koru-nova-frontend-router-shell";
import { KoruLogo } from "@spinning-top/koru-nova-frontend-design-system";
import {
  createCookieTokenStore,
  createLocalStorageTokenStore,
} from "@spinning-top/koru-nova-frontend-auth";
import authModule from "@spinning-top/koru-nova-frontend-module-auth";
import usersModule from "@spinning-top/koru-nova-frontend-module-user";
import rbacModule from "@spinning-top/koru-nova-frontend-module-rbac";
import baseDataModule from "@spinning-top/koru-nova-frontend-module-base-data";
import settingsModule from "@spinning-top/koru-nova-frontend-module-settings";
import emailTemplateModule from "@spinning-top/koru-nova-frontend-module-email-template";
import pdfModule from "@spinning-top/koru-nova-frontend-module-pdf";
import { createDocumentsModule } from "@spinning-top/koru-nova-frontend-module-documents";
import privacyModule from "@spinning-top/koru-nova-frontend-module-privacy";
import { createEventsModule } from "@spinning-top/koru-nova-frontend-module-events";
import { createNotificationModule } from "@spinning-top/koru-nova-frontend-module-notification";
import businessRegistryModule from "@spinning-top/koru-nova-frontend-business-registry";
import auditModule from "@spinning-top/koru-nova-frontend-module-audit";
import emailModule from "@spinning-top/koru-nova-frontend-module-email";
import jobModule from "@spinning-top/koru-nova-frontend-module-job";
import backupModule from "@spinning-top/koru-nova-frontend-module-backup";
import { createSearchModule } from "@spinning-top/koru-nova-frontend-module-search";
import { createCalendarModule } from "@spinning-top/koru-nova-frontend-module-calendar";
import type { SalusCalendarSourceContext } from "@spinning-top/koru-salus-frontend-base-data-common";
import normsModule, { normsMenuIcons } from "@spinning-top/koru-salus-frontend-module-norms";
import testTypeCategoriesModule, {
  testTypeCategoriesMenuIcons,
} from "@spinning-top/koru-salus-frontend-module-test-type-categories";
import activitiesModule, {
  activitiesMenuIcons,
} from "@spinning-top/koru-salus-frontend-module-activities";
import surveyTypesModule, {
  surveyTypesMenuIcons,
} from "@spinning-top/koru-salus-frontend-module-survey-types";
import examinationTypesModule, {
  examinationTypesMenuIcons,
} from "@spinning-top/koru-salus-frontend-module-examination-types";
import serviceTypesModule, {
  serviceTypesMenuIcons,
} from "@spinning-top/koru-salus-frontend-module-service-types";
import vaccinationTypesModule, {
  vaccinationTypesMenuIcons,
} from "@spinning-top/koru-salus-frontend-module-vaccination-types";
import examinationHintsModule, {
  examinationHintsMenuIcons,
} from "@spinning-top/koru-salus-frontend-module-examination-hints";
import risksModule, { risksMenuIcons } from "@spinning-top/koru-salus-frontend-module-risks";
import testTypesModule, {
  testTypeExternalCodesSupplierTab,
  testTypesMenuIcons,
} from "@spinning-top/koru-salus-frontend-module-test-types";
import examinationLimitationsModule, {
  examinationLimitationsMenuIcons,
} from "@spinning-top/koru-salus-frontend-module-examination-limitations";
import examinationPrescriptionsModule, {
  examinationPrescriptionsMenuIcons,
} from "@spinning-top/koru-salus-frontend-module-examination-prescriptions";
import dutyTemplatesModule, {
  dutyTemplatesMenuIcons,
} from "@spinning-top/koru-salus-frontend-module-duty-templates";
import preferencesModule from "@spinning-top/koru-salus-frontend-module-preferences";
import legalEntitiesModule, {
  legalEntitiesMenuIcons,
  useSelectedLegalEntity,
} from "@spinning-top/koru-salus-frontend-module-legal-entities";
import brokerModule, { brokersMenuIcons } from "@spinning-top/koru-salus-frontend-module-broker";
import {
  createSupplierModule,
  suppliersMenuIcons,
} from "@spinning-top/koru-salus-frontend-module-supplier";
import doctorModule, { doctorsMenuIcons } from "@spinning-top/koru-salus-frontend-module-doctor";
import {
  companiesMenuIcons,
  createCompanyModule,
} from "@spinning-top/koru-salus-frontend-module-company";
import {
  branchesMenuIcons,
  companyBranchesTab,
  createBranchModule,
} from "@spinning-top/koru-salus-frontend-module-branch";
import {
  createBranchEmployeesTab,
  createCompanyEmployeesTab,
  createEmployeeModule,
  employeesMenuIcons,
} from "@spinning-top/koru-salus-frontend-module-employee";
import employeeExpirationModule, {
  employeeExpirationsTab,
} from "@spinning-top/koru-salus-frontend-module-employee-expiration";
import employeeContractModule, {
  employeeContractsTab,
} from "@spinning-top/koru-salus-frontend-module-employee-contract";
import employeeDutyModule, {
  employeeDutiesTab,
} from "@spinning-top/koru-salus-frontend-module-employee-duty";
import employeeExemptionModule, {
  employeeExemptionsTab,
} from "@spinning-top/koru-salus-frontend-module-employee-exemption";
import employeeVaccinationModule, {
  employeeVaccinationsTab,
  examinationVaccinationsSection,
} from "@spinning-top/koru-salus-frontend-module-employee-vaccination";
import companyDutiesModule, {
  companyDutiesTab,
} from "@spinning-top/koru-salus-frontend-module-company-duties";
import companyContractsModule, {
  companyContractsTab,
} from "@spinning-top/koru-salus-frontend-module-company-contracts";
import companyCostListModule, {
  companyCostListsTab,
} from "@spinning-top/koru-salus-frontend-module-company-cost-list";
import companyPriceListModule, {
  companyPriceListsTab,
} from "@spinning-top/koru-salus-frontend-module-company-price-list";
import {
  branchExaminationsTab,
  companyExaminationsTab,
  createExaminationModule,
  employeeExaminationsTab,
  examinationMenuIcons,
  examinationWorkerAction,
} from "@spinning-top/koru-salus-frontend-module-examination";
import examinationSessionModule, {
  examinationSessionMenuIcons,
} from "@spinning-top/koru-salus-frontend-module-examination-session";
import externalTestModule, {
  branchExternalTestsTab,
  companyExternalTestsTab,
  employeeExternalTestsTab,
  externalTestMenuIcons,
} from "@spinning-top/koru-salus-frontend-module-external-test";
import externalTestSessionModule, {
  externalTestSessionMenuIcons,
} from "@spinning-top/koru-salus-frontend-module-external-test-session";
import serviceModule, {
  serviceMenuGroupIcons,
  serviceMenuIcons,
} from "@spinning-top/koru-salus-frontend-module-service";
import schedulingModule, {
  schedulingMenuIcons,
} from "@spinning-top/koru-salus-frontend-module-scheduling";
import costListModule, {
  costListMenuGroupIcons,
  costListMenuIcons,
} from "@spinning-top/koru-salus-frontend-module-cost-list";
import priceListModule, {
  priceListMenuGroupIcons,
  priceListMenuIcons,
} from "@spinning-top/koru-salus-frontend-module-price-list";
import invoiceModule, {
  invoiceMenuGroupIcons,
  invoiceMenuIcons,
} from "@spinning-top/koru-salus-frontend-module-invoice";
import analyticsModule, {
  analyticsMenuGroupIcons,
  analyticsMenuIcons,
} from "@spinning-top/koru-salus-frontend-module-analytics";
import dashboardModule from "@spinning-top/koru-salus-frontend-module-dashboard";
import companyStatsModule, {
  branchStatsTab,
  companyStatsTab,
} from "@spinning-top/koru-salus-frontend-module-company-stats";
import connectorModule from "@spinning-top/koru-salus-frontend-connector";
// The Credits/Info page (`/about`) is LOCAL and replaces the kit's `module-about`: the
// kit draws its own "KORU Nova" lockup there, wordmark and gradient wired inside
// `<KoruLogo>` with no slot to override them. Same module name, same route, same rail
// footer entry — only the brand on the page is this product's.
import aboutModule from "./about/index.ts";
import { env } from "./env.ts";

const useCookie = env.VITE_AUTH_MODE === "cookie";

// Documents: the "scopes" (owner types) selectable in the binding matrix of the
// document-type catalogue. `value` matches the backend `ownerType` EXACTLY (the
// owner resolvers registered in `backend/boilerplate/src/main.ts`); `labelKey` is
// resolved from the `documents` namespace.
//
// ⚠️ The Salus registries that have BOTH a backend document owner resolver (see the
// backend boilerplate `main.ts`) and a frontend module: `broker`, `supplier`, `doctor`,
// `company` and `branch` (NOT `company-safety` / `branch-safety`: those tokens anchor the
// safety referents only). Each label is contributed by ITS OWN module's i18n bundle, not by
// the kit, which ships `owner_types.customer` only (and KORU Salus has no customers):
// without the module mounted the matrix column header would show the raw key.
//
// NO `ownerNamespace` / `documentsPath` on any owner type, and no `expiriesContextParams`: they
// serve ONLY the kit's «Scadenze documenti» source of the Calendar (`documents.expiries`, which the
// kit builds only when at least one owner type declares `ownerNamespace`), and Marco took it out of
// the Calendar on 2026-11-04. Every `<DocumentsSection>` of a registry detail passes its own
// `ownerNamespace` prop: the documents of the registries are untouched. Putting the source back
// means putting the three fields back (`git log -p` of this file, Salus FE 0.2.74).
const documentsModule = createDocumentsModule({
  ownerTypes: [
    { value: "broker", labelKey: "documents:owner_types.broker" },
    { value: "supplier", labelKey: "documents:owner_types.supplier" },
    { value: "doctor", labelKey: "documents:owner_types.doctor" },
    { value: "company", labelKey: "documents:owner_types.company" },
    { value: "branch", labelKey: "documents:owner_types.branch" },
    { value: "employee", labelKey: "documents:owner_types.employee" },
  ],
});

// Events: the factory instead of the default export, and here it is MANDATORY — the
// kit's default covers `customer` alone, which does not exist in KORU Salus, so the
// binding matrix would offer a scope the backend does not register (an unknown token
// is a 422, not an empty row).
//
// Same list as the documents one above, seen from the other end, but the two must NOT
// be merged into one shared constant: they are two distinct registries on the backend,
// and the day an entity is documentable but not eventable the single constant would be
// telling a lie about one of them.
const eventsModule = createEventsModule({
  ownerTypes: [
    { value: "broker", labelKey: "events:owner_types.broker" },
    { value: "supplier", labelKey: "events:owner_types.supplier" },
    { value: "doctor", labelKey: "events:owner_types.doctor" },
    { value: "company", labelKey: "events:owner_types.company" },
    { value: "branch", labelKey: "events:owner_types.branch" },
    { value: "employee", labelKey: "events:owner_types.employee" },
  ],
});

// Notifications: the factory instead of the default export, for the `subjectType → path`
// map alone. A document-expiry notification has the OWNER as its subject (`subjectType`
// = the owner-type token, `subjectId` its id) and the backend knows none of this app's
// routes: without this map the feed is exactly what it was, with rows that are not
// clickable. The keys are the backend tokens VERBATIM — identities, not labels — and are
// the same owner types declared above, seen from the other end. A `subjectType` not
// listed leaves the row non-clickable, which is the normal state of a notification
// without a subject.
const notificationModule = createNotificationModule({
  subjectRoutes: {
    broker: (id) => `/brokers/${id}`,
    supplier: (id) => `/suppliers/${id}`,
    doctor: (id) => `/doctors/${id}`,
    company: (id) => `/companies/${id}`,
    branch: (id) => `/branches/${id}`,
    employee: (id) => `/employees/${id}`,
  },
});

// The global search filtered on the ditta of the rail (Marco's decision of 2026-10-27, kit
// `module-search` ≥ 19.2.0): every `GET /search` of the results page carries `legalEntityId`, in
// the query key and never in the URL. While the selection is still loading, `useContextReady`
// holds the search back (the page shows its loader, no request leaves), so a cold `/search?q=`
// never shows the hits of every ditta first. Once it has settled, `undefined` — no
// `legalEntity.lookup`, a failed lookup, a doctor with no incarico — is not sent, and the backend
// answers on the caller's perimeter as before. HOOKS, called by the module's `Providers` inside
// the route of `/search`: the selection reads auth, permissions and Query, all mounted at the
// root by the shell, so it resolves there as on every page. ⚠️ Both read the SAME selection in
// the same render: a `ready` ahead of its `legalEntityId` would send the search unfiltered. The
// filter is the backend's: one that does not declare `legalEntityId` drops it and answers
// unfiltered.
type SearchContextParams = { legalEntityId: string | undefined };
const useSearchContextParams = (): SearchContextParams => ({
  legalEntityId: useSelectedLegalEntity().id ?? undefined,
});
const useSearchContextReady = (): boolean => !useSelectedLegalEntity().isLoading;
const searchModule = createSearchModule({
  useContextParams: useSearchContextParams,
  useContextReady: useSearchContextReady,
});

// The Calendar view of the kit (`module-calendar` 1.1.0: `/calendar` in the rail group
// `agenda`, and the «Calendari» section of the base-data hub), mounted by its factory on the
// ditta of the rail, as the search above: every calendar source gets `{ legalEntityId }` as
// `ctx.context` and narrows it itself, putting it in its OWN query key (the calendar does not).
// Serializable on purpose: the kit compares it by content (a query-key hash), so the fresh
// object of every render does not count as a change. While the selection is loading,
// `useSourceContextReady` keeps every source silent and the page in its loader; the custom
// calendars of the catalogue do not depend on it. Same HOOKS rule as the search: called by the
// module's `Providers` around its own routes, and the selection resolves there because it reads
// only auth, permissions and Query, mounted at the root by the shell. Three Salus sources read it
// (backend `0.1.78`): the visits of `module-examination` and the prestazioni of `module-service`,
// one calendar per doctor of the ditta in his group, and the deadlines of `module-scheduling`
// among the «Predefiniti»; with `legalEntityId: null` they show nothing and ask nothing.
// The shape the three Salus sources narrow with `calendarSourceLegalEntityId` (base-data-common).
const useCalendarSourceContext = (): SalusCalendarSourceContext => ({
  legalEntityId: useSelectedLegalEntity().id,
});
const useCalendarSourceContextReady = (): boolean => !useSelectedLegalEntity().isLoading;
const calendarModule = createCalendarModule({
  useSourceContext: useCalendarSourceContext,
  useSourceContextReady: useCalendarSourceContextReady,
});

// The actions on ONE worker another module owns, through `module-employee`'s worker-action port:
// today «Crea visita medica» of `module-examination` — the ONLY way into the guided create of a
// visit (Marco's decision: the visits list has no «Crea»). The arc runs examination → employee,
// so the APP joins the halves, and hands the SAME array to the three hosts that list workers:
// the `/employees` list and the worker detail (`createEmployeeModule`), the «Lavoratori» tab of
// the company and the one of the sede (the two tab factories below).
const workerActions = [examinationWorkerAction];

// The «Lavoratori» tabs of the company and of the sede, built ONCE with the worker actions.
const companyEmployeesTab = createCompanyEmployeesTab({ workerActions });
const branchEmployeesTab = createBranchEmployeesTab({ workerActions });

// The company module WITH its one port wired: the sub-entity and related-registry tabs of
// the company detail, whose descriptors and tables come from the modules that OWN them —
// `module-branch` ("Sedi"), `module-employee` ("Lavoratori"), `module-company-contracts`
// ("Incarichi"), `module-company-duties` ("Mansioni"), `module-company-cost-list` ("Listini
// costi"), `module-company-price-list` ("Listini prezzi"), `module-examination` ("Visite
// mediche"), `module-external-test` ("Accertamenti esterni") and `module-company-stats`
// ("Statistiche"). The domain arcs run from each of them to the company, so the
// company cannot import any of them: the APP joins the halves.
// Built here rather than imported as the default export, which is the same module with no
// ports at all.
//
// ORDER MATTERS and it is KORU Salus OLD's: `… safety-referents, branches, employees,
// contracts, duties, cost-lists, price-lists, examinations, external-tests, statistics, …`, so
// "Lavoratori" and then "Incarichi" go BETWEEN "Sedi" and "Mansioni", the two listini follow,
// costs first, then "Visite mediche" and "Accertamenti esterni", and "Statistiche" closes the
// block. Where the block of contributed tabs sits as a whole is `module-company`'s decision, not
// the app's (after the company's own data tabs, before the kit blocks).
const companyModule = createCompanyModule({
  detailTabs: [
    companyBranchesTab,
    companyEmployeesTab,
    companyContractsTab,
    companyDutiesTab,
    companyCostListsTab,
    companyPriceListsTab,
    companyExaminationsTab,
    companyExternalTestsTab,
    companyStatsTab,
  ],
});

// The worker module WITH the same port, opened here for the SECOND parent of the sub-entity
// category: `module-employee-contract` owns the "Contratti di lavoro" tab and
// `module-employee` owns the slot, because the arc runs employee-contract → employee. Built
// here rather than imported as the default export, which is the same module with no ports.
//
// Seven contributed tabs in OLD's order: contratti, mansioni, esenzioni, vaccinazioni, quadri
// scadenze, visite mediche and accertamenti esterni — the last two contributed by
// `module-examination` and `module-external-test` to the company and the branch as well. Where the block
// sits as a whole is `module-employee`'s decision, not the app's (after "Generale" and
// "Contatti", before the kit blocks).
const employeeModule = createEmployeeModule({
  workerActions,
  detailTabs: [
    employeeContractsTab,
    employeeDutiesTab,
    employeeExemptionsTab,
    employeeVaccinationsTab,
    employeeExpirationsTab,
    employeeExaminationsTab,
    employeeExternalTestsTab,
  ],
});

// The branch module WITH the port it opened for the THIRD parent: FIRST the «Lavoratori» of
// `module-employee` (arc employee → branch; OLD had no such tab, it is where «Crea visita medica»
// starts from a sede), then OLD's «Visite mediche» and «Accertamenti esterni» tabs of the sede
// detail, in that order, owned by `module-examination` and `module-external-test` (the arcs run
// examination → branch and external-test → branch), then «Statistiche» of
// `module-company-stats` (arc company-stats → branch), which OLD did not have on the sede: LAST
// of the block, as on the company. Built here rather than imported as the default export, which
// is the same module with no ports.
const branchModule = createBranchModule({
  detailTabs: [branchEmployeesTab, branchExaminationsTab, branchExternalTestsTab, branchStatsTab],
});

// The supplier module WITH the port it opened for the FOURTH parent: the «Codici esterni» of
// `module-test-types` — the code with which the supplier, as an external laboratory, calls
// each of our test-types — read-only on this side. The arc runs test-types → supplier, so the
// supplier cannot import the tab: the APP joins the halves. Built here rather than imported
// as the default export, which is the same module with no ports. Where the block sits is
// `module-supplier`'s decision (after its own data tabs, before the kit blocks).
const supplierModule = createSupplierModule({
  detailTabs: [testTypeExternalCodesSupplierTab],
});

// The visits module WITH its section port (Marco's decision of 2026-10-11): the worker's
// «Vaccinazioni» of `module-employee-vaccination`, right after «Esame obiettivo» in the visit's
// edit form (create, edit and delete in dialogs) and in its detail (read-only). In the backend
// neither module depends on the other, so neither imports the other — not even for a type: the
// APP joins the halves, and this call is where TypeScript checks that the contribution has the
// port's shape. Where the section sits is `module-examination`'s decision. Built here rather
// than imported as the default export, which is the same module with no sections.
const examinationModule = createExaminationModule({
  sections: [examinationVaccinationsSection],
});

export const App = createApp({
  modules: [
    authModule,
    usersModule,
    rbacModule,
    // "Base data" hub (`/base-data`): aggregates the `baseDataSections` of the Salus
    // modules below. No visible section → rail entry hidden.
    baseDataModule,
    // The three REGISTRY blocks a registry module (module-broker, module-supplier,
    // module-doctor, module-company below) mounts
    // around its detail page. All three contribute sections to the base-data hub
    // above and need it mounted: without it they mount without error and simply show
    // nothing.
    //   - `documentsModule`: reusable documents block (`<DocumentsSection>` in an
    //     owner's detail) + the document-type catalogue section, with the scopes
    //     declared above;
    //   - `privacyModule`: three sections (processing purposes, contact usages,
    //     notices) and `<ConsentSection>`, the per-owner consent panel. Its endpoints
    //     declare `auth.session`: the real permission is
    //     `<ownerNamespace>.consent.{read,write}`, resolved by the owner's backend
    //     resolver and declared by the registry module, not by privacy;
    //   - `eventsModule`: the event-TYPE catalogue section, with its own matrix. There
    //     is no UI for the events themselves — the per-owner timeline is
    //     `<EventsSection>`, which the broker detail mounts.
    //
    // ⚠️ `FEModule.Providers` (the owner-type context of documents/events) is wired by
    // the shell around the OWNING module's routes only, so inside the base-data hub —
    // a route of `module-base-data` — the matrix sees no owner types. Same wiring as
    // KORU Salus OLD; check it by hand in the boilerplate before relying on it.
    //
    documentsModule,
    privacyModule,
    eventsModule,
    // `notificationModule`: the feed (`/notifications`) and the per-kind switches of
    // `/account/notifications`. The document-expiry KIND LABELS of each registry
    // (`notification:kind.documents.expiry.<ownerType>.*`) come from that registry's own
    // bundle: the backend declares those kinds parametric on the owner type and no kit
    // fallback makes the raw key presentable.
    notificationModule,
    // `searchModule`: the results page of the global search (`/search`, no menu entry). The
    // shell's topbar field and ⌘K navigate to `/search?q=` on Enter: without this module the
    // router answers with its 404. The categories, names and icons come from the
    // `searchableTypes` the Salus modules declare. Built by the factory, so that every
    // `GET /search` carries the ditta of the rail (see `useSearchContextParams`).
    searchModule,
    // `calendarModule`: the Calendar view (`/calendar`), a direct button of the rail (its group
    // `agenda` holds that one entry, and the shell draws a one-entry group as a plain link), and
    // the «Calendari» section of the base-data hub above, which it needs mounted. Built by the
    // factory, on the ditta of the rail (see `useCalendarSourceContext`).
    calendarModule,
    // `businessRegistryModule`: i18n-only module that registers the `business_registry`
    // namespace of the P.IVA prefill field the registry forms mount. The field switches
    // itself off when the backend module is not enabled (404 on its lookup), but the
    // labels come from here.
    businessRegistryModule,
    // The bottom of the rail. `auditModule`, `emailModule`, `jobModule` and
    // `backupModule` put their entries in the `maintenance` group, which
    // `menuFooterGroups` below moves to the bottom: without at least one of them that
    // option has nothing to move, and the footer zone is not drawn at all. `aboutModule`
    // adds the info button (`/about`) under it, a `railFooterEntries` visible to every
    // authenticated user — and it is the LOCAL one (`./about`), not the kit's: same name
    // and same route, with the KORU Salus lockup instead of the KORU Nova one.
    auditModule,
    emailModule,
    jobModule,
    backupModule,
    aboutModule,
    normsModule,
    testTypeCategoriesModule,
    activitiesModule,
    surveyTypesModule,
    examinationTypesModule,
    serviceTypesModule,
    vaccinationTypesModule,
    examinationHintsModule,
    risksModule,
    testTypesModule,
    examinationLimitationsModule,
    examinationPrescriptionsModule,
    dutyTemplatesModule,
    // Legal entities (Ditte) are mounted further down, after the kit's PDF sections: see there.
    // Brokers (Intermediari), the first multi legal entity REGISTRY: a top-level
    // `/brokers` rail entry in the `registry` group, NOT a section of the Base data hub.
    // It needs `legalEntitiesModule` (its list is scoped on the legal entity selected in
    // the rail) and the three registry blocks above.
    brokerModule,
    // Suppliers (Fornitori), the second one and the broker's twin: same wiring, same
    // obligations, its own `/suppliers` routes and its own rail entry in `registry`.
    supplierModule,
    // Doctors (Medici), the third one, a natural person: same wiring and obligations, its
    // own `/doctors` routes and rail entry, plus the account grant — which assigns the
    // role chosen in the Settings section (`preferences.doctorRoleId`) — and the signature.
    doctorModule,
    // Companies (Aziende), the fourth one: same wiring and obligations, its own
    // `/companies` routes and rail entry (first of the group, as in OLD), plus the type
    // with the public-procurement codes, the ATECO code, the safety referents and the
    // managing broker — whose select reads `brokerModule`'s lookup above. The per-company
    // statistics of OLD are a tab contributed by `companyStatsModule` below, not this module's;
    // the contract strips of the list are not here: the backend serves those, but this list
    // does not consume them yet (see `app-modules.test.tsx`). Mounted here WITH its tabs wired
    // (see `companyModule` above).
    companyModule,
    // Branches (Sedi), the fifth one and the first WITHOUT a legal entity: its scope comes
    // from the parent company, its archival is a column of the row, and it has no consents
    // at all (nine runtime permissions, not eleven — so `branch` is NOT an owner type of
    // `privacyModule`). Its own `/branches` routes and rail entry, plus the contextual
    // create page under `/companies/:id/branches/new` and the "Sedi" tab above. It needs
    // `companyModule`; its «Statistiche» tab is contributed by `companyStatsModule` below, and
    // the contract flags of the list are not here: served by the backend, not consumed.
    // Mounted here WITH its tabs wired (see `branchModule` above).
    branchModule,
    // Employees (Lavoratori), the sixth one and the second of a NATURAL PERSON after the
    // doctors: same wiring and obligations, its own `/employees` routes and rail entry, plus
    // the "Lavoratori" tab of the company detail above. It is the ONE registry whose list is
    // not global — `GET /employees` requires an azienda (or the `none` perimeter) and, under
    // a company, the ditta of the rail — so it needs `legalEntitiesModule` mounted and reads
    // the lookups of `companyModule` and `branchModule` for its two filters. Archival is per
    // (lavoratore, azienda, ditta), behind the `employee.archive` leaf, and never a field of
    // the PUT. A worker IS a data subject, so `employee` is an owner type of `privacyModule`
    // too, unlike `branch`. Under a real azienda the list (and the company and sede
    // «Lavoratori» tabs) draws «Mansioni attive» (`withActiveDuties`) and «Visita medica» —
    // the per-sede health-surveillance verdict (`withExamStatus`), the Scadenzario's rule —
    // beside the document «Conformità» (`withCompliance`); all three are served by the backend
    // inside its package, so nothing else needs mounting for them. Mounted here WITH the
    // "Contratti di lavoro" tab wired (see `employeeModule` above).
    employeeModule,
    // Employment contracts (Contratti di lavoro): the SECOND sub-entity of an anagrafica,
    // and the first hanging off the WORKER instead of the company — no rail entry, no hub
    // section, no global list, nothing searchable. It lives inside the worker: the
    // "Contratti di lavoro" tab above plus the three contextual routes under
    // `/employees/$employeeId/work-contracts/*`. Mounting it without passing
    // `employeeContractsTab` to the factory would leave those routes reachable by URL only,
    // with no error anywhere. It needs `employeeModule`, and it reads the lookups of
    // `companyModule` (the employer, GLOBALLY: a contract is scoped by no ditta) and
    // `branchModule` (the sedi, scoped on the employer chosen).
    employeeContractModule,
    // Duties (Mansioni del lavoratore): the NINTH sub-entity of an anagrafica and the fifth
    // hanging off the WORKER. The "Mansioni" tab above plus the three contextual routes under
    // `/employees/$employeeId/duties/*`. Mounting it without passing `employeeDutiesTab` to
    // the factory would leave those routes reachable by URL only, with no error anywhere. Its
    // employers and sedi come from the worker's CONTRACTS (`GET /employee-contracts`, an HTTP
    // read, no import); it reads the lookups of `companyDutiesModule` (and the company-duty
    // detail, the source of the auto-fill), `risksModule`, `testTypesModule` and
    // `surveyTypesModule`. Its writes refresh the «Mansioni» column of the workers tables.
    employeeDutyModule,
    // Exemptions (Esenzioni): the FIFTH sub-entity of an anagrafica and the fourth hanging
    // off the WORKER — the simplest of them: a calendar window (open-ended when it has no
    // end date) and a free-text reason, which is HEALTH data. No certificate, no lookup to
    // read, no legal entity. The "Esenzioni" tab above plus the three contextual routes
    // under `/employees/$employeeId/exemptions/*`. Mounting it without passing
    // `employeeExemptionsTab` to the factory would leave those routes reachable by URL
    // only, with no error anywhere. It needs `employeeModule`.
    employeeExemptionModule,
    // Vaccinations (Vaccinazioni): the THIRD sub-entity of an anagrafica and the second
    // hanging off the WORKER — same shape as the contracts above (no rail entry, no hub
    // section, no global list, nothing searchable) plus a single optional CERTIFICATE. It
    // lives inside the worker: the "Vaccinazioni" tab above plus the three contextual
    // routes under `/employees/$employeeId/vaccinations/*`. Mounting it without passing
    // `employeeVaccinationsTab` to the factory would leave those routes reachable by URL
    // only, with no error anywhere. It needs `employeeModule`, and it reads the lookup of
    // `vaccinationTypesModule` for the vaccine picker of its form. ⚠️ The certificate is
    // health data and is invisible to `/files` for EVERYBODY, `*` included: its three
    // endpoints, behind three dedicated permission leaves, are the only way to it.
    employeeVaccinationModule,
    // Expiry schedules (Quadri scadenze): the FOURTH sub-entity of an anagrafica and the
    // third hanging off the WORKER — same shape as the two above, with no certificate and
    // four set-complete link lists. The "Quadri scadenze" tab above plus the three
    // contextual routes under `/employees/$employeeId/expirations/*`. Mounting it without
    // passing `employeeExpirationsTab` to the factory would leave those routes reachable by
    // URL only, with no error anywhere. Its employer picker reads the worker's CONTRACTS
    // (`GET /employee-contracts`, an HTTP read, no import), and it reads the lookups of
    // `branchModule`, `testTypesModule` and `surveyTypesModule` plus the limitation and
    // prescription lookups (replicated on their owners' keys). Its expiry badge carries its
    // own i18n fragment: mounting documents is not needed for the badge.
    employeeExpirationModule,
    // Company duties (Mansioni aziendali): NOT a registry and not base data — no rail
    // entry, no hub section, no global list, nothing searchable. It lives inside the
    // company: the "Mansioni" tab above plus the three contextual routes under
    // `/companies/$companyId/duties/*`. Mounting it without passing `companyDutiesTab` to
    // the factory would leave those routes reachable by URL only, with no error anywhere.
    // It needs `companyModule`, and it reads the lookups of `risksModule`,
    // `testTypesModule` and `surveyTypesModule` for the three linked lists of its form.
    companyDutiesModule,
    // Company contracts (Incarichi): the SIXTH sub-entity of an anagrafica and the second of
    // the COMPANY — no rail entry, no hub section, no global list, nothing searchable. It lives
    // inside the company: the "Incarichi" tab above plus the three contextual routes under
    // `/companies/$companyId/contracts/*`, with the nomine mediche as a tab of the incarico
    // and «Concludi» / «Riapri» as actions. Mounting it without passing `companyContractsTab`
    // to the factory would leave those routes reachable by URL only, with no error anywhere.
    // It is scoped on the ditta of the RAIL (the tab lists that ditta's incarichi, a create is
    // for it), so it needs `legalEntitiesModule`; it reads the lookups of `branchModule` (the
    // sedi of the company), `doctorModule` (the doctor of a nomina, scoped on the incarico's
    // ditta) and the activities (replicated on `activitiesModule`'s key). The Aziende list
    // does NOT consume the contracts port yet: see `app-modules.test.tsx`.
    companyContractsModule,
    // Company cost lists and price lists (Listini costi / Listini prezzi di un'azienda): the
    // SEVENTH and EIGHTH sub-entities, the third and fourth of the COMPANY, structural twins —
    // the assignment of a listino to the company over a window of dates, for the ditta of the
    // RAIL. The two tabs above plus three contextual routes each under
    // `/companies/$companyId/{cost-lists,price-lists}/*`. Mounting them without passing their
    // tabs to the factory would leave those routes reachable by URL only. Their pickers read
    // the list queries of `costListModule` and `priceListModule` on those modules' keys, and
    // they need `legalEntitiesModule` for the scope.
    companyCostListModule,
    companyPriceListModule,
    // Statistics (Statistiche): the read-only dashboards of a company and of a sede, in NONE of
    // the five categories — no route at all, no rail entry, no hub section, nothing searchable,
    // and not a sub-entity either (it owns no record). It is the two «Statistiche» tabs above
    // (`companyStatsTab`, `branchStatsTab`), and it is mounted for its i18n namespace and for
    // the permissions it declares (`company.stats.read`, `branch.stats.read`): without it the
    // tabs show raw keys and no role can be granted them. Scoped on the ditta of the RAIL, so it
    // needs `legalEntitiesModule`; the backend serves the two endpoints only with its two
    // `statsProvider` ports wired (its boilerplate does).
    companyStatsModule,
    // Scheduling (Scadenzario): the health-surveillance worklist, an OPERATIONAL module and the
    // FIRST entry of the `services` group (KORU Salus OLD's order: 1, before the visits). One
    // read-only `/scheduling` route with two row units (company + branch, worker + branch), for
    // the ditta of the RAIL, so it needs `legalEntitiesModule`; the doctor badge strip comes
    // from base-data-common. Owns no entity: no search category, no trash, no
    // write.
    schedulingModule,
    // Medical examinations (Visite mediche): the fourth OPERATIONAL module and the FIRST entry of
    // the `services` group (KORU Salus OLD's order: 5, before the external tests). A GUIDED
    // create (dialog), a detail and an edit of thirteen sections, the three report slots, the
    // per-test report and the free attachments — the files are written from the detail only.
    // Scoped on the rail's ditta, so it needs `legalEntitiesModule`; it reads the lookups of
    // `companyModule`, `branchModule`, `employeeModule`, `doctorModule` and of the risks, test
    // and survey types, and contributes the «Visite mediche» tab to the worker, the company and
    // the branch (see the factories above). ⚠️ Every file of a visit is health data and is
    // invisible to `/files`: its own endpoints are the only way to it.
    examinationModule,
    // Examination sessions (Sessioni di visite mediche): the fifth OPERATIONAL module, the
    // per-day plan of worker entries that the GENERATE turns into visits, all or nothing.
    // Second entry of the `services` group (OLD's 6). List, create, detail and edit — the
    // create is this port's, OLD had none — editable and generable while `draft` only, HARD
    // delete. Scoped on the rail's ditta; reads the lookups of `companyModule`, `branchModule`,
    // `doctorModule` and `employeeModule`, and needs `examinationModule` MOUNTED: its bundle
    // translates the `examination.*` codes the generate surfaces verbatim.
    examinationSessionModule,
    // External tests (Accertamenti esterni): the second OPERATIONAL module, a test carried out
    // by an external supplier on a worker. Its own `/external-tests` routes and a rail entry
    // in the `services` group, BEFORE the sessions and the prestazioni (KORU Salus OLD's
    // order: 7, 8, 10). List, detail and EDIT (the schede of its types, backend 0.1.43), NO
    // create and NO delete by design; it writes the supplier's report from the detail. Scoped
    // on the ditta of the RAIL, so it needs `legalEntitiesModule`; it reads the lookups of
    // `companyModule`, `branchModule` and `employeeModule` for its filters and of
    // `testTypesModule` for its edit, and contributes the «Accertamenti esterni» tab to the
    // three of them (see the factories above). ⚠️ The report is health data and is invisible
    // to `/files` for EVERYBODY: its three endpoints are the only way to it.
    externalTestModule,
    // External-test sessions (Sessioni di accertamenti esterni): the third OPERATIONAL module,
    // the dated container of a batch of tests. List, detail, edit and delete, NO create (by
    // design). Scoped on the rail's ditta; reads the lookups of `companyModule`,
    // `branchModule` and `supplierModule`. Its detail deep-links to the tests list filtered
    // on the session — a URL, not an import.
    externalTestSessionModule,
    // Services (Prestazioni): the first OPERATIONAL module — not a registry and not base
    // data, but the record of what has been DELIVERED. Its own `/services` routes and a rail
    // entry in a NEW group, `services`, ordered right after `registry` below. The ditta comes
    // from the rail and never from a select, so it needs `legalEntitiesModule` mounted; it
    // reads the lookups of `companyModule`, `branchModule`, `doctorModule` and
    // `serviceTypesModule` for its pickers and filters. No trash bin, no full-text search and
    // no runtime permissions: a prestazione is not an anagrafica.
    serviceModule,
    // Cost lists (Listini costi): the first FINANCE module — not an anagrafica, not base data,
    // not something that happened, but the tariff of what the studio pays out. Its own
    // `/cost-lists` routes and a rail entry in the `financial` group ("Finanza", KORU Salus
    // OLD's), ordered right after `services` below. The ditta comes from the rail, so it needs
    // `legalEntitiesModule`; the subject pickers read the lookups of `doctorModule` (scoped on
    // the list's ditta) and `supplierModule`, the target pickers the four taxonomies on their
    // own keys. No trash bin, no full-text search.
    costListModule,
    // Price lists (Listini prezzi): the second FINANCE module, twin of the cost lists — what the
    // studio charges. `/price-lists` routes and a rail entry in the same `financial` group,
    // after the cost lists (`order: 20`, KORU Salus OLD's). The ditta comes from the rail; the
    // price lines carry no subject, so no doctor or supplier lookup — only the four taxonomy
    // lookups of the target pickers. Besides the lines: payment schedule, annual charges and
    // fixed quotas. No trash bin, no full-text search.
    priceListModule,
    // Invoices (Fatture): the third FINANCE module — not a third list but a fiscal DOCUMENT of
    // one ditta, addressed to a company XOR a broker. `/invoices` routes (list, trash, the two
    // pages of the "to be invoiced" report, create, detail, edit) and a rail entry in the same
    // `financial` group, after the two lists (`order: 30`, KORU Salus OLD's); the report has no
    // rail entry, it opens from the "Genera fattura" button of the list. The ditta comes from
    // the rail; the recipient pickers read the lookups of `companyModule` and `brokerModule`;
    // `legalEntitiesModule` also translates `legalEntity.not_found`, one of the three 404s of
    // the print. A REAL trash bin, and the invoice category of the global search. It also
    // contributes «Fatturazione» to the config hub below (backend 0.1.77): per ditta, the flag
    // of the annuities and the numbering of the current year; its icon is in `invoiceMenuIcons`.
    invoiceModule,
    // Reports («Resoconti»): in none of the five categories — read-only reports over what the
    // operational modules recorded. ONE page, `/analytics/reports`, the report chosen in
    // `?report=`, and a rail entry «Resoconti» in the KIT's `analytics` group («Analisi»), ordered
    // right after `financial` below and drawn as a direct button while it is alone there. The
    // ditta comes from the rail, so it needs `legalEntitiesModule`; aziende and medici of its
    // pickers are read by HTTP (no arc towards `companyModule`/`doctorModule`).
    analyticsModule,
    // Config hub (`/settings`) and its "Settings" section. The Salus module, NOT the
    // kit's `module-preferences` default export: that one carries the base schema
    // and its PUT would miss the mandatory Salus day counts. The hub has two more Salus
    // sections, «Template di stampa», contributed by `legalEntitiesModule` below, and
    // «Fatturazione», contributed by `invoiceModule` above.
    settingsModule,
    preferencesModule,
    // The other two things the config hub carried in KORU Salus OLD. Neither has a rail
    // entry: each contributes `settingsSections` only (their CRUD routes are deep links
    // from the section tables), so without `settingsModule` above they would mount and
    // show nothing. Their icons (`mail`, `file-text`, `panel-top`, `panel-bottom`,
    // `blocks`) are in the kit's curated set: no `menuIcons` to merge.
    //   - `emailTemplateModule`: the "E-mail templates" section (API `/email-templates`,
    //     `email_template.read/write`; pages under `/settings/email-templates/*`);
    //   - `pdfModule`: four sections (PDF templates, headers, footers, blocks; API
    //     `/pdf/{templates,headers,footers,blocks}`, `pdf.<resource>.*`).
    // Both are served by the Salus backend. Together with the Settings section they are
    // what OLD's "Sistema" carried.
    emailTemplateModule,
    pdfModule,
    // Legal entities (Ditte): hub section + CRUD routes, the rail selector of the current
    // legal entity with its per-entity theme (a `topbarWidget` gated on `legalEntity.lookup`,
    // shown only with more than one legal entity), and the «Template di stampa» section of the
    // config hub (backend 0.1.57: per ditta and per kind of printed document, the PDF template of
    // `pdfModule` it prints with instead of the app's). Needs `AddressProvider` at the root
    // (`main.tsx`) and the rail gradient built from `--rail-gradient-from/to` (`index.css`).
    // Mounted HERE, right after `pdfModule`: a choice of THIS boilerplate, for its census
    // (`app-modules.test.tsx` reads the config-hub sections in mounting order, and wants the
    // ditte's print templates after the four PDF ones). It has no effect at runtime — the kit's
    // hub sorts its sections by label — and binds no derived app: nothing of this module depends
    // on its position, the modules above that read its hooks and lookup only need it mounted.
    legalEntitiesModule,
    // Dashboard: the HOME. Its route `/` replaces the kit shell's placeholder (the shell drops
    // its own `/` as soon as a module declares an authenticated one), and it declares NO
    // permission: every card gates itself on the permissions of the module it reads, and without
    // any the page shows its empty state instead of a 403 after login. It reads the endpoints of
    // the Scadenzario, the invoices, the two session modules and the six registries through its
    // own facade — no import from them — and LINKS to their pages, which is why they are mounted
    // above; the ditta comes from the rail, so it needs `legalEntitiesModule`.
    dashboardModule,
    // The local connector of the doctor's workstation (`ws://127.0.0.1:47800`): its indicator
    // in the topbar, gated on `examination.update` (who fills in the visits launches the
    // instruments). It opens NOTHING until the user turns it on from that indicator, per
    // browser; turned on, the page's origin (http://localhost:5173 here) must be among the
    // connector's allowed origins, or it reads "unreachable".
    connectorModule,
  ],
  menuIcons: {
    ...normsMenuIcons,
    ...testTypeCategoriesMenuIcons,
    ...activitiesMenuIcons,
    ...surveyTypesMenuIcons,
    ...examinationTypesMenuIcons,
    ...serviceTypesMenuIcons,
    ...vaccinationTypesMenuIcons,
    ...examinationHintsMenuIcons,
    ...risksMenuIcons,
    ...testTypesMenuIcons,
    ...examinationLimitationsMenuIcons,
    ...examinationPrescriptionsMenuIcons,
    ...dutyTemplatesMenuIcons,
    ...legalEntitiesMenuIcons,
    ...brokersMenuIcons,
    ...suppliersMenuIcons,
    ...doctorsMenuIcons,
    ...companiesMenuIcons,
    ...branchesMenuIcons,
    // `id-card-lanyard` is OUTSIDE the kit's curated set too: same silent `Circle` fallback
    // in the rail and in the global-search results without this line.
    ...employeesMenuIcons,
    // `briefcase-business` is OUTSIDE the kit's curated set (which has the plain
    // `briefcase`): without this line `resolveMenuIcon` would degrade to `Circle` in
    // silence, in the rail and in the global-search results alike.
    ...serviceMenuIcons,
    // `clock-alert`, `stethoscope`, `calendar-clock`, `flask-conical` and `calendar-check` are
    // OUTSIDE the kit's curated set as well.
    ...schedulingMenuIcons,
    ...examinationMenuIcons,
    ...examinationSessionMenuIcons,
    ...externalTestMenuIcons,
    ...externalTestSessionMenuIcons,
    // `hand-coins` and `wallet` ARE in the kit's curated set: merged all the same, so the
    // module keeps its icons the day that set changes.
    ...costListMenuIcons,
    // `tags` and `wallet` are in the curated set too: same reason.
    ...priceListMenuIcons,
    // `piggy-bank` (the entry) and `receipt-euro` (the global-search category) are OUTSIDE the
    // kit's curated set: without this line both degrade to `Circle` in silence.
    ...invoiceMenuIcons,
    // `chart-column` (the «Resoconti» entry and the `analytics` group button) is OUTSIDE the kit's
    // curated set, which has the legacy `bar-chart-3`: without this line both degrade to `Circle`.
    ...analyticsMenuIcons,
  },
  apiBaseUrl: env.VITE_API_BASE_URL,
  tokenStore: useCookie ? createCookieTokenStore() : createLocalStorageTokenStore(),
  authMode: useCookie ? "cookie" : "bearer",
  i18n: {
    defaultLocale: env.VITE_DEFAULT_LOCALE,
    supportedLocales: env.VITE_SUPPORTED_LOCALES,
  },
  theme: { defaultMode: env.VITE_THEME_DEFAULT_MODE },
  // BRANDING: Esseci Consulenza's, from Esseci OLD — not the product's. `publicLogo` is the
  // lockup of every PUBLIC surface — login, password reset, expired password, 403, 404, the
  // error view and the client-access gate — which the kit otherwise draws as its own "KORU
  // Nova" lockup: the customer's logo (`public/logo-esseci.svg`, symbol + wordmark, in its
  // colours) through `<KoruLogo src>`, over the Esseci blue of `--brand-public*` in
  // `index.css`. `logo` is the mark at the top of the rail: the symbol alone, all white
  // (`public/logo-esseci-mark.svg`), because the rail is the blue gradient and the colour
  // symbol would vanish into it. Both files are crops of `design/logo-esseci-source.svg`,
  // which `src/favicon.test.tsx` holds them to.
  //
  // What stays the PRODUCT's: `appName` — the brand of the topbar, the breadcrumb root and
  // the rail tooltip, whose default is "KORU Nova" — and the local About page, which credits
  // KORU Salus and its maker, as Esseci OLD kept the platform's brand on its About. The boot
  // screen (`slots.restoring`) carries no mark at all: only the spinner over the same
  // `--brand-public*` gradient, already Esseci blue.
  slots: {
    appName: "KORU Salus",
    publicLogo: <KoruLogo src="/logo-esseci.svg" size={64} alt="Esseci Consulenza" />,
    logo: <img src="/logo-esseci-mark.svg" alt="Esseci Consulenza" className="h-[32px] w-[32px]" />,
  },
  // `services` is contributed by `serviceModule` and is NOT one of the kit's four known
  // groups: it must be listed here to be ordered (right after the registries, as in KORU
  // Salus OLD), and its LABEL comes from that module's i18n bundle.
  // `financial` likewise, contributed by `costListModule`, `priceListModule` and
  // `invoiceModule`, right after `services` as in KORU Salus OLD.
  // `analytics` is a kit group («Analisi»), contributed by `analyticsModule`, right after
  // `financial`; its button icon is overridden to `chart-column` below.
  // `agenda` is a kit group (label and `CalendarDays` icon from the kit) and holds the Calendar
  // alone, so the rail draws it as a direct button with no flyout; listed FIRST, so it sits
  // right under the shell's own Dashboard button (Marco's decision of 2026-10-31).
  menuGroupOrder: ["agenda", "registry", "services", "financial", "analytics", "system"],
  // ...and the icon of its rail BUTTON, which the kit has no default for either: an unknown
  // group falls back on the neutral `LayoutGrid`. The name (`microscope`) rides in the
  // module's `menuIcons` merged above, because the override is resolved through the same
  // registry as `MenuEntry.icon` — unregistered it would degrade to `Circle`, not to
  // `LayoutGrid`.
  menuGroupIcons: {
    ...serviceMenuGroupIcons,
    ...costListMenuGroupIcons,
    ...priceListMenuGroupIcons,
    ...invoiceMenuGroupIcons,
    ...analyticsMenuGroupIcons,
  },
  menuFooterGroups: ["maintenance"],
  devtools: false,
});
